import React, { useCallback, useEffect, useState } from 'react';
import type {
	Collection,
	CollectionUUID,
} from '@jira/polaris__domain-collection/src/SrcViewResponse.tsx';
import {
	getCollectionDetailsPageUrl,
	getCollectionDetailsPageUrlWithQuery,
	getCollectionsPageUrl,
	getCollectionViewPageUrl,
} from '@jira/polaris__component-collections/src/getCollectionsPageUrl.tsx';
import { useCollectionsActions } from '@jira/polaris__component-collections/src/useCollectionsActions.tsx';
import { useNavigationCollectionsActions } from '@jira/polaris__component-navigation-collections-store/src/useNavigationCollectionsActions.tsx';
import { CollectionDeleteModal } from '@jira/polaris__component-collection-delete-modal/src/CollectionDeleteModal.tsx';
import { useManageViewPermissions } from '@jira/polaris__component-permissions-store/src/useCanManageViews.tsx';
import { graphql, useFragment } from 'react-relay';
import type { OnCloseProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import ButtonItem from '@atlaskit/menu/button-item';
import type { ButtonItemProps } from '@atlaskit/menu/types';
import DeleteIcon from '@atlaskit/icon/core/delete';
import CopyIcon from '@atlaskit/icon/core/copy';
import SettingsIcon from '@atlaskit/icon/core/settings';
import type { KeyboardOrMouseEvent } from '@atlaskit/modal-dialog/types';
import { token } from '@atlaskit/tokens';
import { Text } from '@atlaskit/primitives/compiled/text';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import {
	useCollectionSelectedMatch,
	useIsCollectionsSelected,
	useIsCollectionSelected,
} from './common/utils/router.tsx';

import { useIsReadOnly } from './common/utils/use-is-read-only.tsx';
import { RoadmapFavoriteSubmenu } from './RoadmapFavoriteSubmenu.tsx';
import type { RoadmapMenuItems_collection$key } from './__generated__/RoadmapMenuItems_collection.graphql.ts';

const roadmapSettings = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.actions-buttons-roadmap.roadmap-settings',
	defaultMessage: 'Roadmap settings',
	description:
		'Label used for the item visible after opening dropdown menu with actions for a specific roadmap',
});

const roadmapDetails = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.actions-buttons-roadmap.roadmap-details',
	defaultMessage: 'Roadmap details',
	description:
		'Label used for the item visible after opening dropdown menu with actions for a specific roadmap',
});

const duplicateRoadmap = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.actions-buttons-roadmap.duplicate-roadmap',
	defaultMessage: 'Duplicate roadmap',
	description:
		'Label used for the item visible after opening dropdown menu with actions for a specific roadmap',
});

const deleteRoadmap = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.actions-buttons-roadmap.delete-roadmap',
	defaultMessage: 'Delete roadmap',
	description:
		'Label used for the item visible after opening dropdown menu with actions for a specific roadmap',
});

const copiedCollectionNameMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.copied-collection-name',
	defaultMessage: '{collectionName} Copy',
	description: 'When user copy roadmap new name would be set to this name.',
});

type RoadmapMenuItemsProps = {
	collectionUUID: CollectionUUID;
	preloadPermissions: boolean;
	onClose: OnCloseProps['onClose'];
	collectionQueryRefFragment?: RoadmapMenuItems_collection$key;
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
};

export const RoadmapMenuItems: React.FC<RoadmapMenuItemsProps> = ({
	collectionUUID,
	preloadPermissions,
	onClose,
	collectionQueryRefFragment,
	currentItems,
	currentOrder,
	item,
}: RoadmapMenuItemsProps) => {
	const collectionRoadmapMenuItemsData = useFragment<RoadmapMenuItems_collection$key>(
		graphql`
			fragment RoadmapMenuItems_collection on JiraProductDiscoveryCollection {
				...RoadmapFavoriteSubmenu_collection
			}
		`,
		collectionQueryRefFragment,
	);
	const [collectionTemporaryData, setCollectionTemporaryData] = useState<Collection | undefined>();
	const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] = useState(false);
	const [isDeleteConfirmationDialogLoading, setIsDeleteConfirmationDialogLoading] = useState(true);
	const { formatMessage } = useIntl();
	const collectionSelectedMatch = useCollectionSelectedMatch();
	const isCollectionsSelected = useIsCollectionsSelected();
	const { push, replace } = useRouterActions();
	const { getCollectionData, copyCollection } = useCollectionsActions();
	const {
		copyCollection: copyCollectionInNavigation,
		deleteCollection: deleteCollectionFromNavigation,
	} = useNavigationCollectionsActions();
	const copiedCollectionOwnerId = useAccountId();
	const collectionName = collectionTemporaryData?.name || '';

	useEffect(() => {
		getCollectionData(collectionUUID)
			.then((data) => {
				setCollectionTemporaryData(data);
				setIsDeleteConfirmationDialogLoading(false);
			})
			.catch((error: Error) => {
				setIsDeleteConfirmationDialogLoading(false);
				fireErrorAnalytics({
					error,
					meta: {
						id: 'jpdNavigationV4Action',
					},
					attributes: {
						action: 'getCollectionData',
						type: 'collections',
					},
				});
			});
	}, [collectionUUID, getCollectionData]);

	const copiedCollectionName = formatMessage(copiedCollectionNameMessage, {
		collectionName,
	});

	const isReadOnly = useIsReadOnly();
	const { canManageView, loadViewPermissionsForCurrentUser } =
		useManageViewPermissions(collectionUUID);

	useEffect(() => {
		if (preloadPermissions) {
			loadViewPermissionsForCurrentUser(collectionUUID);
		}
	}, [loadViewPermissionsForCurrentUser, collectionUUID, preloadPermissions]);

	const canEditCollection = !isReadOnly && canManageView;

	const isCollectionSelected = useIsCollectionSelected({ collectionUUID });

	const openSidebar = useCallback(
		(sidebar: 'about' | 'settings') => {
			if (
				collectionSelectedMatch?.collectionUUID === collectionUUID &&
				collectionSelectedMatch?.viewUUID
			) {
				replace(
					getCollectionViewPageUrl(collectionUUID, collectionSelectedMatch.viewUUID, {
						sidebar,
					}),
				);
			} else if (isCollectionsSelected) {
				replace(getCollectionsPageUrl({ sidebar, roadmap: collectionUUID }));
			} else {
				push(getCollectionDetailsPageUrlWithQuery(collectionUUID, { sidebar }));
			}
		},
		[collectionSelectedMatch, collectionUUID, isCollectionsSelected, push, replace],
	);

	const handleRoadmapSettingsClick: NonNullable<ButtonItemProps['onClick']> = useCallback(
		(event) => {
			onClose?.(event);
			openSidebar('settings');
		},
		[onClose, openSidebar],
	);

	const handleCloneRoadmapClick: NonNullable<ButtonItemProps['onClick']> = useCallback(
		(event) => {
			copyCollection(
				collectionUUID,
				copiedCollectionName,
				copiedCollectionOwnerId,
				collectionTemporaryData,
			).then((copiedCollectionUuid) => {
				if (copiedCollectionUuid) {
					copyCollectionInNavigation(collectionUUID, {
						uuid: copiedCollectionUuid,
						name: copiedCollectionName,
						ownerId: copiedCollectionOwnerId,
					});

					push(getCollectionDetailsPageUrl(copiedCollectionUuid));
				}
			});

			onClose?.(event);
		},
		[
			collectionTemporaryData,
			collectionUUID,
			copiedCollectionName,
			copiedCollectionOwnerId,
			copyCollection,
			copyCollectionInNavigation,
			onClose,
			push,
		],
	);

	const handleDeleteRoadmapClick: NonNullable<ButtonItemProps['onClick']> = useCallback(() => {
		setIsDeleteConfirmationDialogOpen(true);
	}, []);

	const handleDeleteRoadmapModalClose = useCallback(
		(event: KeyboardOrMouseEvent) => {
			setIsDeleteConfirmationDialogOpen(false);
			onClose?.(event);
		},
		[onClose],
	);

	const handleDeleteRoadmapSuccess = useCallback(
		(event: KeyboardOrMouseEvent) => {
			deleteCollectionFromNavigation(collectionUUID);

			if (isCollectionSelected) {
				// If we are on removed collection then redirected
				// to all Roadmaps page instead of showing
				// `This roadmap isn't available screen`
				push(getCollectionsPageUrl());
			} else {
				handleDeleteRoadmapModalClose(event);
			}
		},
		[
			collectionUUID,
			deleteCollectionFromNavigation,
			isCollectionSelected,
			push,
			handleDeleteRoadmapModalClose,
		],
	);

	return (
		<>
			<MenuGroup>
				{collectionRoadmapMenuItemsData && (
					<RoadmapFavoriteSubmenu
						collectionQueryRefFragment={collectionRoadmapMenuItemsData}
						currentItems={currentItems}
						currentOrder={currentOrder}
						item={item}
						onClose={onClose}
					/>
				)}
				<Section isList hasSeparator>
					{canEditCollection ? (
						<ButtonItem
							iconBefore={<SettingsIcon label="" spacing="spacious" />}
							onClick={handleRoadmapSettingsClick}
						>
							{formatMessage(roadmapSettings)}
						</ButtonItem>
					) : (
						<ButtonItem
							iconBefore={<AlignTextLeftIcon label="" spacing="spacious" />}
							onClick={handleRoadmapSettingsClick}
						>
							{formatMessage(roadmapDetails)}
						</ButtonItem>
					)}
					{!isReadOnly && (
						<ButtonItem
							iconBefore={<CopyIcon label="" spacing="spacious" />}
							onClick={handleCloneRoadmapClick}
						>
							{formatMessage(duplicateRoadmap)}
						</ButtonItem>
					)}
				</Section>
				{canEditCollection && (
					<Section isList hasSeparator>
						<ButtonItem
							iconBefore={
								<DeleteIcon label="" color={token('color.icon.danger')} spacing="spacious" />
							}
							onClick={handleDeleteRoadmapClick}
						>
							<Text color="color.text.danger">{formatMessage(deleteRoadmap)}</Text>
						</ButtonItem>
						{isDeleteConfirmationDialogOpen && (
							<CollectionDeleteModal
								collectionUUID={collectionUUID}
								isLoading={isDeleteConfirmationDialogLoading}
								views={collectionTemporaryData?.views || []}
								onDelete={handleDeleteRoadmapSuccess}
								onClose={handleDeleteRoadmapModalClose}
								collectionName={collectionTemporaryData?.name}
							/>
						)}
					</Section>
				)}
			</MenuGroup>
		</>
	);
};
